// CloudKeeper Org Services Portal - AWS IAM Access Analyzer API client.
// Uses portalFetch from portalApi.js (JWT stored as portal_token).
// Mirrors inspectorApi.js / macieApi.js: every call resolves to the
// {success,data} envelope.
//
// portalFetch gates on `response.ok`, so the 201 that POST /config, POST /refresh
// and POST /roles/status answer resolves exactly like a 200 - no call here needs a
// status special case. Any non-2xx instead REJECTS with an Error carrying the
// backend's `error` string, which is the only signal a caller gets: there is no
// status code on the thrown Error. Where a specific status is part of an
// endpoint's contract rather than a fault, it is called out on that function.
//
// SHAPE NOTE: this file deliberately uses the `BASE` prefix form rather than
// guarddutyApi.js's inlined full paths. The route prefix is the one string that
// has to agree with the Go route registration and with the engine descriptor's
// id ('accessanalyzer'); keeping exactly one copy of it means a rename cannot
// half-land across eighteen call sites.
import { portalFetch } from './portalApi';

const BASE = '/api/v1/securityhub/accessanalyzer';

// --- config ---

// RESOLVES with `data: null` for a tenant that has never enabled Access
// Analyzer - HTTP 200, not 404. The service reports that state as (nil, nil),
// exactly as Inspector and Detective do, and the handler passes it straight
// through.
//
// That resolved null IS the setup-wizard signal, so callers branch on
// `data === null` rather than on a rejection. Making this route 404 instead was
// considered and rejected during design: it would have made every page component
// handle a rejection where the five sibling engines handle a null, and any page
// that forgot would show an error banner to every un-enabled tenant.
//
// A rejection from this call is therefore a REAL fault (500), not the pre-enable
// state. Note this is deliberately unlike getAccessAnalyzerSummary below, which
// does 404 when unconfigured - /config owns the enabled/disabled question, and
// the summary route is not the place to answer it.
export const getAccessAnalyzerConfig = () =>
  portalFetch(`${BASE}/config`);

// body: { homeRegion, linkingRegions, memberAccountIds, unusedAccessEnabled,
//         unusedAccessAge }
//
// unusedAccessEnabled defaults to false on purpose - the ACCOUNT_UNUSED_ACCESS
// analyzer family is BILLED PER IAM PRINCIPAL PER MONTH, unlike the free
// external-access family, so it is opt-in and must never be turned on implicitly
// by a caller that simply omitted the field.
export const enableAccessAnalyzer = (body) =>
  portalFetch(`${BASE}/config`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

// Teardown deletes only the analyzers Prism created (identified by the Prism
// tag). Analyzers that were already present when enrollment adopted them are
// left running - deleting one destroys all of ITS findings, which belong to the
// customer, not to Prism.
export const disableAccessAnalyzer = () =>
  portalFetch(`${BASE}/config`, { method: 'DELETE' });

// External-access analyzers are per (account × linking region), so dropping a
// region here also drops that scope's cached findings server-side. The unused
// access analyzer is NOT affected: it lives at the home region only.
export const updateAccessAnalyzerRegions = (linkingRegions) =>
  portalFetch(`${BASE}/config/regions`, {
    method: 'PUT',
    body: JSON.stringify({ linkingRegions }),
  });

// Access Analyzer's analogue of Macie's discovery toggle / Inspector's resource
// types, and the only paid switch in this capability: `enabled` creates or tears
// down one ACCOUNT_UNUSED_ACCESS analyzer per enrolled account AT THE HOME REGION
// ONLY (it is region-independent - fanning it across linking regions would
// multiply the customer's bill for identical findings).
//
// `age` is the unused-access tracking period in days; the backend normalises it,
// so passing an out-of-range number is corrected rather than rejected.
export const updateAccessAnalyzerUnusedAccess = (enabled, age) =>
  portalFetch(`${BASE}/config/unused-access`, {
    method: 'PUT',
    body: JSON.stringify({ enabled, age }),
  });

// --- members ---

export const listAccessAnalyzerMembers = () =>
  portalFetch(`${BASE}/members`);

// No memberEmails counterpart to addMacieMembers/addDetectiveMembers: the flat
// peer model creates an ACCOUNT-scoped analyzer in each account directly through
// that account's own switch role, so there is no invitation to address and no
// root-email resolution step before enrollment.
export const addAccessAnalyzerMembers = (accountIds) =>
  portalFetch(`${BASE}/members`, {
    method: 'POST',
    body: JSON.stringify({ accountIds }),
  });

// Account only, unlike retryMacieMember's (account, region) pair: one retry
// re-drives every failed cell the account owns - each linking region's external
// access analyzer plus, when the paid family is on, its home-region unused
// access analyzer.
export const retryAccessAnalyzerMember = (accountId) =>
  portalFetch(`${BASE}/members/retry`, {
    method: 'POST',
    body: JSON.stringify({ accountId }),
  });

// Removes the account from enrollment AND deletes that account's cached findings
// - leaving them behind was a shipped GuardDuty bug, where findings for a
// de-enrolled account kept counting toward the dashboard totals forever.
export const removeAccessAnalyzerMember = (accountId) =>
  portalFetch(`${BASE}/members/${encodeURIComponent(accountId)}`, {
    method: 'DELETE',
  });

// --- findings ---

export const listAccessAnalyzerFindings = (params = {}) => {
  const qs = new URLSearchParams();
  // severity and status are the only multi-valued filters; the handler splits
  // them on "," and drops blanks. accountId is SINGLE-valued - passing a
  // comma-joined list would be matched literally and return nothing.
  //
  // Severity is MANUFACTURED BY PRISM: Access Analyzer ships no severity field
  // at all. The values are still the shared five (CRITICAL|HIGH|MEDIUM|LOW|
  // INFORMATIONAL) from securityhub/severity.js, never Inspector's UNTRIAGED.
  if (params.severity?.length) qs.set('severity', params.severity.join(','));
  // ACTIVE | ARCHIVED - AWS's finding status, not a Prism workflow state.
  if (params.status?.length) qs.set('status', params.status.join(','));
  if (params.accountId) qs.set('accountId', params.accountId);
  // `findingType`, NOT Inspector's `type`: the handler reads the key that matches
  // the filter field's JSON tag (models.AccessAnalyzerFindingFilter.FindingType),
  // exactly as Detective's reads `entityType`. Sending `type` here would be
  // silently ignored and return the unfiltered list, which looks like a broken
  // filter rather than a bad request.
  if (params.findingType) qs.set('findingType', params.findingType);
  if (params.search) qs.set('search', params.search);
  // Cursor drives keyset pagination; dropping it pins every page to the first.
  if (params.cursor) qs.set('cursor', params.cursor);
  if (params.limit) qs.set('limit', String(params.limit));
  const q = qs.toString();
  return portalFetch(`${BASE}/findings${q ? `?${q}` : ''}`);
};

// Accepts the row's UUID `id`. Encoding is what keeps AWS-shaped identifiers
// working: Access Analyzer's own finding ids and ARNs carry slashes and colons
// that would otherwise split the path and miss the single-segment "{id}" route,
// i.e. a 404 for every such lookup.
export const getAccessAnalyzerFinding = (id) =>
  portalFetch(`${BASE}/findings/${encodeURIComponent(id)}`);

// --- refresh (the three-part polling contract) ---

// No "kind" discriminator in the body, unlike triggerMacieRefresh: Access
// Analyzer caches only findings, so there is nothing to choose between. One
// refresh sweeps every enrolled account across both analyzer families.
export const triggerAccessAnalyzerRefresh = () =>
  portalFetch(`${BASE}/refresh`, { method: 'POST' });

// Currently running refresh job for this tenant (data: null when none, including
// for an unconfigured tenant) - lets a dashboard re-attach its progress bar after
// navigation or reload. Takes no `kind` parameter, again unlike Macie's.
export const getAccessAnalyzerActiveRefreshJob = () =>
  portalFetch(`${BASE}/refresh/active`);

// Rejects with "record not found" once a job ages out of the table; poll loops
// should stop on rejection rather than retrying forever.
export const getAccessAnalyzerRefreshJob = (jobId) =>
  portalFetch(`${BASE}/refresh/${encodeURIComponent(jobId)}`);

// --- posture ---

// REJECTS (HTTP 404, "IAM Access Analyzer is not configured") when the tenant has
// not enabled the capability, deliberately: an empty summary would render
// identically to "Access Analyzer is on and everything is clean". This matches
// getInspectorSummary and diverges from getMaciePostureSummary, which answers an
// unconfigured tenant with zeroes.
//
// The rejection is left intact rather than mapped to null here - callers that
// want the wizard should branch off getAccessAnalyzerConfig, which is the
// endpoint that owns the enabled/disabled state.
export const getAccessAnalyzerSummary = () =>
  portalFetch(`${BASE}/summary`);

// --- role permissions ("Fix roles", props for RolePermissionsPanel) ---
// Prism keeps a scoped inline policy (CKPrism-AccessAnalyzer-Permissions) on each
// enrolled account's switch role. The scan is async: POST starts (or re-joins) a
// job, GET polls it. Fix is fire-and-forget - re-run a scan to see the outcome.
//
// These three are the shared securityhub roles routes registered under this
// capability's prefix, so they take the same shape as Inspector's and can be
// handed to RolePermissionsPanel unchanged.

export const startAccessAnalyzerRolesScan = () =>
  portalFetch(`${BASE}/roles/status`, { method: 'POST' });

export const getAccessAnalyzerRolesJob = (jobId) =>
  portalFetch(`${BASE}/roles/status/${encodeURIComponent(jobId)}`);

export const fixAccessAnalyzerRoles = () =>
  portalFetch(`${BASE}/roles/fix`, { method: 'POST' });
